import type { CollateralKey } from "@/data/fixtures";
import { FIXTURE } from "@/data/fixtures";
import type { VaultStatusKey } from "@/components/defi/VaultStatusPill";
import { isChainOnlyOracleMarks } from "@/lib/oracle/mode";
import { fetchExternalPriceSamples } from "@/lib/oracle/price-providers";
import { resolvePriceFeedsForEnv } from "@/lib/oracle/resolve-price-feeds";
import {
	getV2ProtocolActor,
	hasProtocolEnv,
	natToAmount,
} from "@/lib/protocol-actor";
import {
	FALLBACK_LIQUIDATION_THRESHOLD,
	FALLBACK_MIN_COLLATERAL_RATIO,
	FALLBACK_RISK_PARAMS,
	type ProtocolRiskParams,
} from "@/lib/vault-risk";
import {
	getPriceFeeds,
	type PriceFeed,
} from "@motokomaker/ic-client";
import { useQuery } from "@tanstack/react-query";

export type PublicVaultSortKey =
	| "healthAsc"
	| "ltvDesc"
	| "debtDesc"
	| "newest";

export type PublicVaultUi = {
	id: bigint;
	ownerLabel: string;
	owner?: string;
	collateral: Array<[CollateralKey, number]>;
	collateralValueUsd: number;
	debt: number;
	ltv: number;
	healthFactor: number;
	status: VaultStatusKey;
	createdNs?: bigint;
	lastUpdatedNs?: bigint;
	source: "live";
};

function sortKeyToCandid(sort: PublicVaultSortKey) {
	switch (sort) {
		case "ltvDesc":
			return { ltvDesc: null };
		case "debtDesc":
			return { debtDesc: null };
		case "healthAsc":
			return { healthAsc: null };
		default:
			return { newest: null };
	}
}

function mapCollateralType(ct: Record<string, null>): CollateralKey {
	const k = Object.keys(ct)[0] ?? "ICP";
	if (k === "ckBTC" || k === "ckETH" || k === "ICP") return k;
	return "ICP";
}

function mapStatus(s: Record<string, null>): VaultStatusKey {
	const k = Object.keys(s)[0] ?? "Active";
	if (k === "AtRisk" || k === "Finished" || k === "Liquidated") return k;
	return "Active";
}

export function useGetPublicVaults(
	limit = 12,
	offset = 0,
	sort: PublicVaultSortKey = "healthAsc",
) {
	return useQuery({
		queryKey: ["v2", "public", "vaults", limit, offset, sort],
		queryFn: async (): Promise<{
			vaults: PublicVaultUi[];
			total: number;
			source: "live" | "empty";
		}> => {
			const actor = await getV2ProtocolActor();
			if (!actor) {
				return { vaults: [], total: 0, source: "empty" };
			}
			const page = await actor.getPublicVaults(
				BigInt(limit),
				BigInt(offset),
				sortKeyToCandid(sort),
			);
			return {
				total: Number(page.total),
				source: "live",
				vaults: page.vaults.map((v) => ({
					id: v.id,
					ownerLabel: v.ownerLabel,
					owner:
						"owner" in v && typeof (v as { owner?: string }).owner === "string"
							? (v as { owner: string }).owner
							: undefined,
					collateral: v.collateral.map(
						([ct, amt]) =>
							[
								mapCollateralType(ct as Record<string, null>),
								natToAmount(amt),
							] as [CollateralKey, number],
					),
					collateralValueUsd: natToAmount(v.collateralValueE8),
					debt: natToAmount(v.debtE8),
					ltv: Number(v.ltvBps) / 100,
					healthFactor: natToAmount(v.healthFactorE8),
					status: mapStatus(v.status as Record<string, null>),
					createdNs:
						"created" in v
							? ((v as { created?: bigint }).created ?? undefined)
							: undefined,
					lastUpdatedNs: v.lastUpdated,
					source: "live" as const,
				})),
			};
		},
		staleTime: 30_000,
		placeholderData: (prev) => prev,
	});
}

export function useGetPublicAnalyticsSummary() {
	return useQuery({
		queryKey: ["v2", "public", "analyticsSummary"],
		queryFn: async () => {
			const fixture = () => ({
				tvl: FIXTURE.tvlUsd,
				totalDebt: FIXTURE.borrowVolumeUsd,
				activeVaults: FIXTURE.vaultCount,
				atRiskCount: FIXTURE.atRiskCount,
				liquidatableCount: FIXTURE.liquidatable.length,
				savingsDeposited: FIXTURE.savingsDeposited,
				borrowApr: FIXTURE.borrowApr as number | null,
				source: "fixture" as const,
			});
			const actor = await getV2ProtocolActor();
			if (!actor) {
				// Offline only — never silent-fixture when env is set
				if (!hasProtocolEnv()) return fixture();
				throw new Error("Protocol actor unavailable");
			}
			// Query path — anonymous SPA cannot call update getAnalyticsSummary (inspect).
			const s = await actor.getAnalyticsSummaryQuery();
			return {
				tvl: natToAmount(s.tvlE8),
				totalDebt: natToAmount(s.totalDebtE8),
				activeVaults: Number(s.activeVaults),
				atRiskCount: Number(s.atRiskCount),
				liquidatableCount: Number(s.liquidatableCount),
				savingsDeposited: natToAmount(s.savingsDepositedE8),
				// Prefer null when chain has no APR field — UI shows unknown
				borrowApr: null as number | null,
				source: "live" as const,
			};
		},
		staleTime: 30_000,
	});
}

export type CollateralMixSlice = {
	asset: CollateralKey;
	sharePct: number;
	lockedUsd: number;
	source: "live" | "fixture";
};

export function useGetPublicCollateralDistribution() {
	return useQuery({
		queryKey: ["v2", "public", "collateralMix"],
		queryFn: async (): Promise<CollateralMixSlice[]> => {
			const fromFixture = (): CollateralMixSlice[] =>
				FIXTURE.collateralMix.map((m) => ({
					...m,
					source: "fixture" as const,
				}));

			const actor = await getV2ProtocolActor();
			if (!actor) {
				if (!hasProtocolEnv()) return fromFixture();
				throw new Error("Protocol actor unavailable");
			}
			const o = await actor.getTreasuryOverview();
			const rows = o.vaultLockedByAsset.map((b) => ({
				asset: mapCollateralType(b.collateralType as Record<string, null>),
				lockedUsd: natToAmount(b.lockedUsdE8),
			}));
			const sum = rows.reduce((a, r) => a + r.lockedUsd, 0) || 1;
			// Empty chain is still live (not a fixture)
			return rows.map((r) => ({
				asset: r.asset,
				lockedUsd: r.lockedUsd,
				sharePct: Math.round((r.lockedUsd / sum) * 100),
				source: "live" as const,
			}));
		},
		staleTime: 60_000,
		retry: hasProtocolEnv() ? 1 : 0,
	});
}

export type AnalyticsHistoryPoint = {
	timestamp: bigint;
	tvl: number;
	totalDebt: number;
	activeVaults: number;
	label: string;
	source: "live" | "fixture";
};

function historyLabel(ts: bigint): string {
	const ms = Number(ts / 1_000_000n);
	if (!Number.isFinite(ms) || ms <= 0) return "—";
	return new Date(ms).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
	});
}

export function useGetPublicAnalyticsHistory(limit = 14) {
	return useQuery({
		queryKey: ["v2", "public", "analyticsHistory", limit],
		queryFn: async (): Promise<AnalyticsHistoryPoint[]> => {
			const fromFixture = (): AnalyticsHistoryPoint[] =>
				FIXTURE.analyticsHistory.slice(-limit).map((p) => ({
					...p,
					label: historyLabel(p.timestamp),
					source: "fixture" as const,
				}));

			const actor = await getV2ProtocolActor();
			if (!actor) {
				if (!hasProtocolEnv()) return fromFixture();
				throw new Error("Protocol actor unavailable");
			}
			const page = await actor.getAnalyticsHistory(0n, BigInt(limit));
			return page.items.map((snap) => ({
				timestamp: snap.timestamp,
				tvl: natToAmount(snap.tvlE8),
				totalDebt: natToAmount(snap.totalDebtE8),
				activeVaults: Number(snap.activeVaults),
				label: historyLabel(snap.timestamp),
				source: "live" as const,
			}));
		},
		staleTime: 60_000,
		retry: hasProtocolEnv() ? 1 : 0,
	});
}

export type CollateralParamsUi = {
	asset: CollateralKey;
	/** Mint floor CR for this asset */
	minCollateralRatio: number;
	maxLtv: number;
	/** Soft-band liquidation threshold CR */
	liqThreshold: number;
	stabilityFeeBps: number;
	debtCeiling: number;
	source: "live" | "fixture";
};

export function useGetPublicCollateralParams() {
	return useQuery({
		queryKey: ["v2", "public", "collateralParams"],
		queryFn: async (): Promise<CollateralParamsUi[]> => {
			const fromFixture = (): CollateralParamsUi[] =>
				FIXTURE.collateralParams.map((p) => ({
					...p,
					minCollateralRatio:
						p.maxLtv > 0 ? 1 / p.maxLtv : FALLBACK_MIN_COLLATERAL_RATIO,
					source: "fixture" as const,
				}));

			const actor = await getV2ProtocolActor();
			if (!actor) {
				if (!hasProtocolEnv()) return fromFixture();
				throw new Error("Protocol actor unavailable");
			}
			const list = await actor.getCollateralParamsList();
			return list.map((p) => {
				const minCr = natToAmount(p.minCollateralRatioE8);
				return {
					asset: mapCollateralType(p.collateralType as Record<string, null>),
					minCollateralRatio: minCr > 0 ? minCr : FALLBACK_MIN_COLLATERAL_RATIO,
					maxLtv: minCr > 0 ? 1 / minCr : 0.66,
					liqThreshold: natToAmount(p.liquidationThresholdE8),
					stabilityFeeBps: Number(p.stabilityFeeBps),
					debtCeiling: natToAmount(p.debtCeiling),
					source: "live" as const,
				};
			});
		},
		staleTime: 60_000,
		retry: hasProtocolEnv() ? 1 : 0,
	});
}

export type ProtocolRiskParamsUi = {
	minCollateralRatio: number;
	liquidationThreshold: number;
	liquidationBonusBps: number;
	savingsRateBps: number;
	globalDebtCeiling: number;
	totalDebt: number;
	mintPaused: boolean;
	liquidatePaused: boolean;
	globalPaused: boolean;
	source: "live" | "fallback";
};

/**
 * Live risk params from getProtocolParams() — governance can retune these.
 * Fallbacks match amounts.mo only when canister is offline.
 */
export function useProtocolRiskParams() {
	return useQuery({
		queryKey: ["v2", "public", "protocolRiskParams"],
		queryFn: async (): Promise<ProtocolRiskParamsUi> => {
			const actor = await getV2ProtocolActor();
			const fallback = (): ProtocolRiskParamsUi => ({
				...FALLBACK_RISK_PARAMS,
				liquidationBonusBps: 500,
				savingsRateBps: 100,
				globalDebtCeiling: 0,
				totalDebt: 0,
				mintPaused: false,
				liquidatePaused: false,
				globalPaused: false,
				source: "fallback",
			});
			if (!actor) {
				if (!hasProtocolEnv()) return fallback();
				throw new Error("Protocol actor unavailable");
			}
			const p = await actor.getProtocolParams();
			const minCollateralRatio = natToAmount(p.minCollateralRatioE8);
			const liquidationThreshold = natToAmount(p.liquidationThresholdE8);
			return {
				minCollateralRatio:
					minCollateralRatio > 0
						? minCollateralRatio
						: FALLBACK_MIN_COLLATERAL_RATIO,
				liquidationThreshold:
					liquidationThreshold > 0
						? liquidationThreshold
						: FALLBACK_LIQUIDATION_THRESHOLD,
				liquidationBonusBps: Number(p.liquidationBonusBps ?? 500n),
				savingsRateBps: Number(p.savingsRateBps ?? 100n),
				globalDebtCeiling: natToAmount(p.globalDebtCeiling),
				totalDebt: natToAmount(p.totalDebt),
				mintPaused: Boolean(p.mintPaused),
				liquidatePaused: Boolean(p.liquidatePaused),
				globalPaused: Boolean(p.globalPaused),
				source: "live",
			};
		},
		staleTime: 30_000,
		retry: hasProtocolEnv() ? 1 : 0,
	});
}

/** Narrow to ProtocolRiskParams for classifyVaultRisk / riskLegend. */
export function toRiskParams(
	p?: Pick<
		ProtocolRiskParamsUi,
		"minCollateralRatio" | "liquidationThreshold"
	> | null,
): ProtocolRiskParams {
	if (!p) return FALLBACK_RISK_PARAMS;
	return {
		minCollateralRatio: p.minCollateralRatio,
		liquidationThreshold: p.liquidationThreshold,
	};
}

export type LiquidatableVaultUi = {
	id: bigint;
	/** Dominant asset for quick label */
	collateral: CollateralKey;
	debtUsdm: number;
	bonusPct: number;
	ltv: number;
	collateralUsd: number;
	/** Per-asset locked amounts + oracle marks */
	collateralLines: Array<{
		asset: CollateralKey;
		amount: number;
		priceUsd: number;
		valueUsd: number;
	}>;
	/** USDM for full clear ≈ debt */
	payUsdm: number;
	/** Max partial repay under close factor (default 50% of debt) */
	maxPartialUsdm: number;
	/** Est. profit = max(0, collUsd - debt) for full clear */
	profitUsd: number;
	cr: number;
	underwater: boolean;
	source: "live";
};

export function useGetPublicLiquidatableVaults() {
	return useQuery({
		queryKey: ["v2", "public", "liquidatable"],
		queryFn: async (): Promise<LiquidatableVaultUi[]> => {
			const actor = await getV2ProtocolActor();
			if (!actor) return [];
			const [list, feeds, protocolParams] = await Promise.all([
				actor.getLiquidatableVaults(),
				actor.getPriceFeeds().catch(() => []),
				actor.getProtocolParams().catch(() => null),
			]);

			const prices: Partial<Record<CollateralKey, number>> = {};
			for (const f of feeds) {
				const asset = mapCollateralType(f.asset as Record<string, null>);
				prices[asset] = natToAmount(f.price);
			}

			let defaultBonusPct = 5;
			if (protocolParams && "liquidationBonusBps" in protocolParams) {
				const bps = Number(protocolParams.liquidationBonusBps);
				if (Number.isFinite(bps) && bps > 0) defaultBonusPct = bps / 100;
			}

			return list.slice(0, 48).map((v) => {
				const debt = natToAmount(v.debt);
				const collPairs = v.collateral.map(
					([ct, amt]) =>
						[
							mapCollateralType(ct as Record<string, null>),
							natToAmount(amt),
						] as [CollateralKey, number],
				);
				const collateralLines = collPairs.map(([asset, amount]) => {
					const priceUsd = prices[asset] ?? 0;
					return {
						asset,
						amount,
						priceUsd,
						valueUsd: amount * priceUsd,
					};
				});
				let collateralUsd = 0;
				let dominant: CollateralKey = collPairs[0]?.[0] ?? "ICP";
				let dominantUsd = 0;
				for (const line of collateralLines) {
					collateralUsd += line.valueUsd;
					if (line.valueUsd >= dominantUsd) {
						dominantUsd = line.valueUsd;
						dominant = line.asset;
					}
				}
				const ltv = collateralUsd > 0 ? (debt / collateralUsd) * 100 : 90;
				const cr = debt > 0 ? collateralUsd / debt : Number.POSITIVE_INFINITY;
				const underwater = collateralUsd < debt;
				const profitUsd = Math.max(0, collateralUsd - debt);
				// Protocol DEFAULT_CLOSE_FACTOR_BPS = 5000 (50%) for soft-band partial
				const maxPartialUsdm = debt * 0.5;
				return {
					id: v.id,
					collateral: dominant,
					debtUsdm: debt,
					bonusPct: defaultBonusPct,
					ltv,
					collateralUsd,
					collateralLines,
					payUsdm: debt,
					maxPartialUsdm,
					profitUsd,
					cr,
					underwater,
					source: "live" as const,
				};
			});
		},
		staleTime: 15_000,
	});
}

export type DutchAuctionUi = {
	id: bigint;
	vaultId: bigint;
	remainingDebtUsdm: number;
	currentPriceUsdm: number;
	startPriceUsdm: number;
	minPriceUsdm: number;
	discountPct: number;
	filled: boolean;
	cancelled: boolean;
	source: "live";
};

function dutchPriceNow(
	startPriceE8: bigint,
	minPriceE8: bigint,
	startTime: bigint,
	endTime: bigint,
): bigint {
	const nowNs = BigInt(Date.now()) * 1_000_000n;
	if (nowNs <= startTime) return startPriceE8;
	if (nowNs >= endTime || endTime <= startTime) return minPriceE8;
	if (startPriceE8 <= minPriceE8) return minPriceE8;
	const span = endTime - startTime;
	const elapsed = nowNs - startTime;
	const drop = ((startPriceE8 - minPriceE8) * elapsed) / span;
	return startPriceE8 <= drop ? minPriceE8 : startPriceE8 - drop;
}

export function useGetLiquidationAuctions() {
	return useQuery({
		queryKey: ["v2", "public", "dutchAuctions"],
		queryFn: async (): Promise<DutchAuctionUi[]> => {
			const actor = await getV2ProtocolActor();
			if (!actor) return [];
			const list = await actor.getLiquidationAuctions();
			return list
				.filter((a) => !a.filled && !a.cancelled)
				.map((a) => {
					const currentE8 = dutchPriceNow(
						a.startPriceE8,
						a.minPriceE8,
						a.startTime,
						a.endTime,
					);
					const remaining = natToAmount(a.debtE8);
					const current = natToAmount(currentE8);
					const start = natToAmount(a.startPriceE8);
					const minP = natToAmount(a.minPriceE8);
					const face = start > 0 ? start : remaining;
					const discountPct =
						face > 0 ? Math.max(0, ((face - current) / face) * 100) : 0;
					return {
						id: a.id,
						vaultId: a.vaultId,
						remainingDebtUsdm: remaining,
						currentPriceUsdm: current,
						startPriceUsdm: start,
						minPriceUsdm: minP,
						discountPct,
						filled: a.filled,
						cancelled: a.cancelled,
						source: "live" as const,
					};
				});
		},
		staleTime: 8_000,
		refetchInterval: 15_000,
	});
}

export type ProposalStatusUi =
	| "active"
	| "passed"
	| "rejected"
	| "executed";

function proposalStatusFromCandid(status: unknown): ProposalStatusUi {
	if (!status || typeof status !== "object") return "active";
	if ("active" in status) return "active";
	if ("passed" in status) return "passed";
	if ("rejected" in status) return "rejected";
	if ("executed" in status) return "executed";
	const key = (Object.keys(status as object)[0] ?? "active").toLowerCase();
	if (key === "active" || key === "pending") return "active";
	if (key === "passed") return "passed";
	if (key === "rejected") return "rejected";
	if (key === "executed") return "executed";
	return "active";
}

export type PublicProposalUi = {
	id: number;
	title: string;
	status: ProposalStatusUi;
	forVotes: number;
	againstVotes: number;
	description: string;
	/** Nanoseconds since epoch (Motoko Time). */
	created: number;
	passedAt: number | null;
};

export function useGetPublicGovernanceProposals() {
	return useQuery({
		queryKey: ["v2", "public", "governance"],
		queryFn: async () => {
			const actor = await getV2ProtocolActor();
			if (!actor)
				return { proposals: [] as PublicProposalUi[], source: "empty" as const };
			const list = await actor.getGovernanceProposals();
			const proposals: PublicProposalUi[] = list.map((p) => {
				const passedRaw = p.passedAt;
				const passedAt =
					Array.isArray(passedRaw) && passedRaw.length > 0
						? Number(passedRaw[0])
						: null;
				return {
					id: Number(p.id),
					title: p.title,
					status: proposalStatusFromCandid(p.status),
					forVotes: natToAmount(p.votesFor),
					againstVotes: natToAmount(p.votesAgainst),
					description: p.description,
					created: Number(p.created),
					passedAt,
				};
			});
			proposals.sort((a, b) => {
				const dc = b.created - a.created;
				if (dc !== 0) return dc;
				return b.id - a.id;
			});
			return { proposals, source: "live" as const };
		},
		staleTime: 60_000,
	});
}

export type OracleFeedUi = {
	asset: CollateralKey;
	price: number;
	lastUpdated: bigint;
	oracleSource: string | null;
	stale: boolean;
	providerCount?: number;
	status?:
		| "fresh"
		| "stale"
		| "pending_osm"
		| "missing"
		| "market_fallback";
	pendingPrice?: number | null;
	pendingActivatesAt?: bigint | null;
};

export function useGetPublicPriceFeeds() {
	const chainOnly = isChainOnlyOracleMarks();
	return useQuery({
		queryKey: ["v2", "public", "prices", chainOnly ? "chain" : "hybrid"],
		queryFn: async () => {
			/** Empty / unavailable — never invent demo prices for play/production. */
			const emptyFeeds = (): OracleFeedUi[] =>
				(["ICP", "ckBTC", "ckETH"] as const).map((asset) => ({
					asset,
					price: 0,
					lastUpdated: 0n,
					oracleSource: null,
					stale: true,
					status: "missing" as const,
				}));

			const actor = await getV2ProtocolActor();
			let chainFeeds: PriceFeed[] = [];
			if (actor) {
				try {
					chainFeeds = await getPriceFeeds(actor);
				} catch {
					chainFeeds = [];
				}
			} else if (!hasProtocolEnv()) {
				// No canister env (e.g. bare Vite without .env) — show empty, not fake marks
				return {
					prices: { ICP: 0, ckBTC: 0, ckETH: 0 },
					feeds: emptyFeeds(),
					source: "unavailable" as const,
				};
			} else {
				throw new Error("Protocol actor unavailable");
			}

			const external = chainOnly
				? ({} as Awaited<ReturnType<typeof fetchExternalPriceSamples>>)
				: await fetchExternalPriceSamples();
			const resolved = resolvePriceFeedsForEnv(
				chainFeeds,
				external,
				chainOnly,
			);

			const prices: Record<CollateralKey, number> = {
				ICP: 0,
				ckBTC: 0,
				ckETH: 0,
			};
			const feeds: OracleFeedUi[] = resolved.map((f) => {
				prices[f.asset] = f.price;
				return {
					asset: f.asset,
					price: f.price,
					lastUpdated: f.lastUpdated,
					oracleSource: f.oracleSource,
					stale: f.status === "stale" || f.status === "missing",
					providerCount: f.providerCount ?? undefined,
					status: f.status,
					pendingPrice: f.pendingPrice,
					pendingActivatesAt: f.pendingActivatesAt,
				};
			});

			// Live = on-chain marks with a positive price. Source tag may be null after
			// protocol upgrade (stable snapshot omits source) — still real chain data.
			const anyLive = feeds.some(
				(f) => f.price > 0 && f.status !== "missing",
			);
			const anyChain = chainFeeds.some((f) => f.price > 0);
			return {
				prices,
				feeds,
				source:
					anyLive || anyChain
						? ("live" as const)
						: ("unavailable" as const),
			};
		},
		staleTime: 30_000,
		refetchInterval: 60_000,
		retry: hasProtocolEnv() ? 1 : 0,
	});
}

/**
 * Certified analytics — badge only when a non-empty certificate is returned.
 * Full cert-tree verify requires noble hashes (production frontend); never fake on fixtures.
 */
export function useCertifiedAnalyticsVerified() {
	return useQuery({
		queryKey: ["v2", "public", "certifiedAnalytics"],
		queryFn: async (): Promise<boolean> => {
			const actor = await getV2ProtocolActor();
			if (!actor) return false;
			try {
				const res = await actor.getCertifiedAnalyticsSummary();
				const cert = res.certificate;
				const len =
					cert instanceof Uint8Array
						? cert.length
						: Array.isArray(cert)
							? cert.length
							: 0;
				return len > 0;
			} catch {
				return false;
			}
		},
		staleTime: 60_000,
	});
}
